import { useCallback } from "react";
import { useNavigate, useLocation, useSearchParams } from "react-router-dom";
import { getPersistedQueryParams } from "@/shared/utils/campaignParams";
import {
  BOOKING_PARAM_KEYS,
  BookingParamName,
  decodeBookingParamValue,
  encodeBookingParamValue,
  LEGACY_ROUTING_PARAMS,
} from "@/shared/utils/bookingUrl";

/**
 * Route/screen live in the path (`/main/seats`), so writing them to the query
 * string as well only made booking URLs longer. Inbound links that still carry
 * them are normalised into path segments at bootstrap
 * (`applyCanonicalBookingUrl`), so nothing downstream needs them in the query.
 */
const QUERY_EXCLUDED_PARAMS = new Set<string>(LEGACY_ROUTING_PARAMS);

const BOOKING_PARAM_NAMES = Object.keys(BOOKING_PARAM_KEYS) as BookingParamName[];

/**
 * True for every spelling of the root path. Callers build target paths by
 * concatenation, so `ROUTES.INDEX` reaches here as `/`, `` and `//` depending
 * on the call site — all of them mean "the experience picker".
 */
const isRootPath = (path: string): boolean => path.split("/").every((segment) => segment === "");

/**
 * Read one booking param, accepting the short wire key and the superseded long
 * name. Empty strings are treated as absent, matching the rest of the flow.
 */
const readParam = (name: BookingParamName, searchParams: URLSearchParams, browserParams: URLSearchParams): string | undefined => {
  const wireKey = BOOKING_PARAM_KEYS[name];
  const raw = searchParams.get(wireKey) || browserParams.get(wireKey) || searchParams.get(name) || browserParams.get(name);

  return raw ? decodeBookingParamValue(name, raw) : undefined;
};

/**
 * Serialise booking params under their short wire keys. Keys that are not
 * booking params (campaign/tracking params merged into the same object) keep
 * their own name and value.
 */
const writeParams = (target: URLSearchParams, source: Record<string, string>): void => {
  Object.entries(source).forEach(([key, value]) => {
    if (QUERY_EXCLUDED_PARAMS.has(key)) return;

    const wireKey = BOOKING_PARAM_KEYS[key as BookingParamName];
    if (wireKey) {
      target.set(wireKey, encodeBookingParamValue(key as BookingParamName, value));
    } else {
      target.set(key, value);
    }
  });
};

/**
 * Re-attach the preserved landing-URL campaign/tracking params (utm_*, gclid,
 * any arbitrary param) to a freshly rebuilt query string.
 *
 * The router rebuilds the URL from a fixed allow-list of booking params on every
 * navigation, which would otherwise strip these. Merging them back here keeps
 * them visible in the address bar throughout the whole flow. Existing params on
 * the target always win, so nothing booking-related is overwritten.
 */
const applyPersistedParams = (urlParams: URLSearchParams): void => {
  const persisted = getPersistedQueryParams();
  Object.entries(persisted).forEach(([key, value]) => {
    if (!urlParams.has(key)) {
      urlParams.set(key, value);
    }
  });
};

interface BookingUrlParams {
  numberOfSeats?: string;
  numberOfRaces?: string;
  selectedDate?: string;
  ageGroup?: string | null;
  homepage?: string;
  redirect?: string;
  selectedRoute?: string;
  selectedScreen?: string;
  postcode?: string;
  // HyperKarting discount params
  discountOffered?: string;
  discount20Applied?: string;
  discount25Applied?: string;
  qualifiedFor20?: string;  // Persistent qualification flag (survives reloads)
  qualifiedFor25?: string;  // Persistent qualification flag (survives reloads)
  qualifiedFor20OnThird?: string;  // Qualified for 20% on 3rd race by going 3→2
  // BattleKart discount params
  bkDiscountOffered?: string;
  bkDiscount20Applied?: string;
  bkDiscount25Applied?: string;
  bkQualifiedFor20?: string;  // Persistent qualification flag (survives reloads)
  bkQualifiedFor25?: string;  // Persistent qualification flag (survives reloads)
  bkQualifiedFor20OnThird?: string;  // Qualified for 20% on 3rd session by going 3→2
  // Multi-track selection params (passed from TrackSelection to RacingTrackSelection)
  includeBK?: string;
  // Mobile session picker variant override (?picker=inline|control|roomy|agenda)
  picker?: string;
}

export const useUrlParams = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();

  // Get current URL parameters
  const getUrlParams = useCallback((): BookingUrlParams => {
    // For MemoryRouter, also check browser URL as fallback
    const browserParams = new URLSearchParams(window.location.search);

    // Extract route and screen from current path
    const pathSegments = location.pathname.split("/").filter((segment) => segment !== "");
    const pathRoute = pathSegments[0] || undefined;
    const pathScreen = pathSegments[1] || undefined;

    const params: Record<string, string | undefined> = {};
    BOOKING_PARAM_NAMES.forEach((name) => {
      params[name] = readParam(name, searchParams, browserParams);
    });

    return {
      ...(params as BookingUrlParams),
      // Route/screen come from the path only. Inbound links carrying them as
      // query params are converted to path segments before the router mounts.
      selectedRoute: pathRoute,
      selectedScreen: pathScreen,
    };
  }, [searchParams, location.pathname]);

  // Update URL parameters while preserving current path
  const updateUrlParams = useCallback(
    (params: Partial<BookingUrlParams>) => {
      // Root is not a booking step, so it has no params to patch. The seats/races
      // screens backfill missing params from an effect keyed on the URL, and the
      // exiting screen stays mounted while the transition to root plays out —
      // without this guard that effect re-fires against the cleared root URL and
      // writes the params straight back on.
      if (isRootPath(location.pathname)) return;

      const currentUrlParams = getUrlParams();
      const updatedParams = { ...currentUrlParams, ...params };

      // Remove undefined/null/empty values
      const cleanParams = Object.entries(updatedParams).reduce((acc, [key, value]) => {
        if (value !== undefined && value !== null && value !== "") {
          acc[key] = value;
        }
        return acc;
      }, {} as Record<string, string>);

      const urlParams = new URLSearchParams();
      writeParams(urlParams, cleanParams);
      applyPersistedParams(urlParams);

      const newSearch = urlParams.toString();

      // Build the path from selectedRoute and selectedScreen
      let newPath = "/";
      if (updatedParams.selectedRoute && updatedParams.selectedRoute !== "") {
        newPath += updatedParams.selectedRoute;
        if (updatedParams.selectedScreen && updatedParams.selectedScreen !== "") {
          newPath += "/" + updatedParams.selectedScreen;
        }
      }

      const newUrl = `${newPath}${newSearch ? `?${newSearch}` : ""}`;

      // Use replace to avoid adding to browser history for parameter updates
      navigate(newUrl, { replace: true });
    },
    [getUrlParams, navigate, location.pathname]
  );

  // Convert path segments to query parameters
  const pathToQueryParams = useCallback((path: string) => {
    const cleanPath = path.startsWith("/") ? path.slice(1) : path;
    const segments = cleanPath.split("/").filter((segment) => segment !== "");

    const pathParams: Partial<BookingUrlParams> = {};

    if (segments.length >= 1 && segments[0]) {
      pathParams.selectedRoute = segments[0];
    }
    if (segments.length >= 2 && segments[1]) {
      pathParams.selectedScreen = segments[1];
    }

    return pathParams;
  }, []);

  // Navigate to a new path while preserving current URL parameters
  const navigateWithParams = useCallback(
    (path: string, additionalParams?: Partial<BookingUrlParams>, replaceOnly?: boolean, navigationState?: Record<string, unknown>) => {
      const currentParams = getUrlParams();
      const pathParams = pathToQueryParams(path);
      const toRoot = isRootPath(path);

      // Root is the start of the flow: nothing has been picked yet, so every
      // booking param carried in from the step we came back from is stale (and
      // `homepage`/`redirect` would re-trigger the deep-link auto-advance).
      // Campaign/tracking params are re-attached further down, so they survive.
      // This mirrors the cleanup QueryBasedRouter runs on the age-group picker.
      let allParams;
      if (toRoot) {
        allParams = { ...additionalParams };
      } else {
        allParams = { ...currentParams, ...pathParams, ...additionalParams };
        if (pathParams.selectedRoute && !pathParams.selectedScreen && !additionalParams?.selectedScreen) {
          allParams.selectedScreen = undefined;
        }
      }

      const cleanParams = Object.entries(allParams).reduce((acc, [key, value]) => {
        if (value !== undefined && value !== null && value !== "") {
          acc[key] = value;
        }
        return acc;
      }, {} as Record<string, string>);

      const urlParams = new URLSearchParams();
      writeParams(urlParams, cleanParams);
      applyPersistedParams(urlParams);

      const newSearch = urlParams.toString();

      // Build the path from selectedRoute and selectedScreen
      let newPath = "/";
      if (!toRoot && allParams.selectedRoute && allParams.selectedRoute !== "") {
        newPath += allParams.selectedRoute;
        if (allParams.selectedScreen && allParams.selectedScreen !== "") {
          newPath += "/" + allParams.selectedScreen;
        }
      }

      const newUrl = `${newPath}${newSearch ? `?${newSearch}` : ""}`;

      // Push a new history entry for real step transitions so the browser Back
      // button can walk back through the flow. Only replace when the caller
      // explicitly opts in (e.g. silent root resets that shouldn't add history).
      navigate(newUrl, { replace: replaceOnly === true, state: navigationState });
    },
    [getUrlParams, navigate, pathToQueryParams]
  );

  return {
    getUrlParams,
    updateUrlParams,
    navigateWithParams,
  };
};
